// src/client/audio.ts — Web Audio 合成：木魚 / 磬 音效 + 生成式禪境氛圍音
// Exposes window.ZenAudio with sfx / tracks / play / stop / setVolume.
import type { Track } from "./types";

interface TrackStopper {
  (): void;
}

interface SynthTrack {
  id: string;
  name: string;
  category: "focus" | "break";
  start(c: AudioContext, bus: AudioNode): TrackStopper;
}

type SynthKey =
  | "stream"
  | "bell"
  | "rain"
  | "wind"
  | "dawn-chime"
  | "sutra-drone"
  | "pine-guqin"
  | "monastery"
  | "aegean-lyre";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let sfxBus: GainNode | null = null;
let ambientBus: GainNode | null = null;
let current: { id: string; stop: TrackStopper } | null = null;
let volume = 0.7;

function ac(): AudioContext {
  if (!ctx) {
    const Ctor = (window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) as
      | typeof AudioContext
      | undefined;
    if (!Ctor) throw new Error("Web Audio not supported in this environment");
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    sfxBus = ctx.createGain();
    sfxBus.gain.value = 0.9;
    sfxBus.connect(master);
    ambientBus = ctx.createGain();
    ambientBus.gain.value = volume;
    ambientBus.connect(master);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

let noiseBuf: AudioBuffer | null = null;
function getNoise(c: AudioContext): AudioBuffer {
  if (!noiseBuf) {
    noiseBuf = c.createBuffer(1, c.sampleRate * 2, c.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  return noiseBuf;
}

function woodfish(when?: number): void {
  const c = ac();
  const t = when ?? c.currentTime;
  const o = c.createOscillator();
  o.type = "sine";
  o.frequency.setValueAtTime(820, t);
  o.frequency.exponentialRampToValueAtTime(540, t + 0.06);
  const g = c.createGain();
  g.gain.setValueAtTime(0.55, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
  o.connect(g).connect(sfxBus!);
  o.start(t);
  o.stop(t + 0.14);
  const n = c.createBufferSource();
  n.buffer = getNoise(c);
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1600;
  bp.Q.value = 2.5;
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.5, t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
  n.connect(bp).connect(ng).connect(sfxBus!);
  n.start(t);
  n.stop(t + 0.07);
}

function bowl(when?: number, base?: number, gainScale?: number): void {
  const c = ac();
  const t = when ?? c.currentTime;
  const f0 = base ?? 520;
  const partials: [number, number, number][] = [
    [1, 0.5, 5.5],
    [2.69, 0.22, 4.0],
    [5.4, 0.08, 2.6],
  ];
  partials.forEach(([ratio, amp, dur]) => {
    [0, 1.7].forEach((detune) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f0 * ratio + detune;
      const g = c.createGain();
      const a = amp * (gainScale ?? 1) * 0.5;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(a, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g).connect(sfxBus!);
      o.start(t);
      o.stop(t + dur + 0.1);
    });
  });
}

function templeBell(c: AudioContext, bus: AudioNode, t: number, f0?: number): void {
  const partials: [number, number, number][] = [
    [1, 0.5, 9],
    [2.0, 0.18, 7],
    [2.92, 0.12, 5],
    [4.1, 0.05, 3.2],
  ];
  partials.forEach(([r, a, d]) => {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = (f0 ?? 130) * r;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(a, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g).connect(bus);
    o.start(t);
    o.stop(t + d + 0.1);
  });
}

function chime(c: AudioContext, bus: AudioNode, t: number, f: number): void {
  const partials: [number, number, number][] = [
    [1, 0.16, 2.2],
    [2.76, 0.05, 1.4],
  ];
  partials.forEach(([r, a, d]) => {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = f * r;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(a, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g).connect(bus);
    o.start(t);
    o.stop(t + d + 0.1);
  });
}

function softDrum(c: AudioContext, bus: AudioNode, t: number, f = 74, amp = 0.34): void {
  const o = c.createOscillator();
  o.type = "sine";
  o.frequency.setValueAtTime(f, t);
  o.frequency.exponentialRampToValueAtTime(f * 0.55, t + 0.22);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(amp, t + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
  o.connect(g).connect(bus);
  o.start(t);
  o.stop(t + 1.35);

  const n = c.createBufferSource();
  n.buffer = getNoise(c);
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 180;
  const ng = c.createGain();
  ng.gain.setValueAtTime(amp * 0.35, t);
  ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
  n.connect(lp).connect(ng).connect(bus);
  n.start(t);
  n.stop(t + 0.24);
}

function pluck(c: AudioContext, bus: AudioNode, t: number, f: number, amp = 0.12): void {
  const o = c.createOscillator();
  o.type = "triangle";
  o.frequency.setValueAtTime(f, t);
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(1800, t);
  lp.frequency.exponentialRampToValueAtTime(420, t + 1.4);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(amp, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
  o.connect(lp).connect(g).connect(bus);
  o.start(t);
  o.stop(t + 2);
}

function droplet(c: AudioContext, bus: AudioNode, t: number): void {
  const f = 700 + Math.random() * 900;
  const o = c.createOscillator();
  o.type = "sine";
  o.frequency.setValueAtTime(f, t);
  o.frequency.exponentialRampToValueAtTime(f * 1.8, t + 0.05);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.08, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
  o.connect(g).connect(bus);
  o.start(t);
  o.stop(t + 0.2);
}

function noiseLayer(
  c: AudioContext,
  bus: AudioNode,
  opts: {
    type: BiquadFilterType;
    freq: number;
    q?: number;
    gain: number;
    lfoRate?: number;
    lfoDepth?: number;
  },
): TrackStopper {
  const src = c.createBufferSource();
  src.buffer = getNoise(c);
  src.loop = true;
  const f = c.createBiquadFilter();
  f.type = opts.type;
  f.frequency.value = opts.freq;
  f.Q.value = opts.q ?? 0.8;
  const g = c.createGain();
  g.gain.value = opts.gain;
  src.connect(f).connect(g).connect(bus);
  let lfo: OscillatorNode | null = null;
  if (opts.lfoRate) {
    lfo = c.createOscillator();
    lfo.frequency.value = opts.lfoRate;
    const lfoG = c.createGain();
    lfoG.gain.value = opts.lfoDepth ?? 0;
    lfo.connect(lfoG).connect(f.frequency);
    lfo.start();
  }
  src.start();
  return () => {
    try {
      src.stop();
      lfo?.stop();
    } catch {
      /* already stopped */
    }
  };
}

function dronePad(c: AudioContext, bus: AudioNode, freqs: number[], gain: number): TrackStopper {
  const stops: TrackStopper[] = [];
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 600;
  const g = c.createGain();
  g.gain.value = gain;
  lp.connect(g).connect(bus);
  freqs.forEach((f) => {
    [-1.3, 1.3].forEach((det) => {
      const o = c.createOscillator();
      o.type = "triangle";
      o.frequency.value = f + det;
      const og = c.createGain();
      og.gain.value = 0.5;
      o.connect(og).connect(lp);
      o.start();
      stops.push(() => {
        try {
          o.stop();
        } catch {
          /* */
        }
      });
    });
  });
  return () => stops.forEach((s) => s());
}

function scheduler(fn: () => void, minMs: number, maxMs: number): TrackStopper {
  let id: ReturnType<typeof setTimeout> | null = null;
  let dead = false;
  const loop = (): void => {
    if (dead) return;
    fn();
    id = setTimeout(loop, minMs + Math.random() * (maxMs - minMs));
  };
  id = setTimeout(loop, 600 + Math.random() * 1200);
  return () => {
    dead = true;
    if (id) clearTimeout(id);
  };
}

const PENTA = [1046.5, 1174.7, 1318.5, 1568, 1760];
const GUQIN = [196, 220, 247, 294, 330, 392];
const LYRE = [261.6, 329.6, 392, 493.9, 587.3, 659.3];

const SYNTH_TRACKS: Record<SynthKey, SynthTrack> = {
  stream: {
    id: "stream",
    name: "山澗流水",
    category: "focus",
    start(c, bus) {
      const s1 = noiseLayer(c, bus, { type: "lowpass", freq: 420, gain: 0.55, lfoRate: 0.13, lfoDepth: 160 });
      const s2 = noiseLayer(c, bus, { type: "bandpass", freq: 1500, q: 0.6, gain: 0.1, lfoRate: 0.21, lfoDepth: 500 });
      const s3 = scheduler(() => droplet(c, bus, c.currentTime), 700, 2600);
      const s4 = scheduler(() => bowlToBus(c, bus, 392, 0.18), 24000, 46000);
      return () => {
        s1();
        s2();
        s3();
        s4();
      };
    },
  },
  bell: {
    id: "bell",
    name: "暮鼓晨鐘",
    category: "focus",
    start(c, bus) {
      const s1 = dronePad(c, bus, [110, 164.8, 220], 0.07);
      const s2 = noiseLayer(c, bus, { type: "lowpass", freq: 240, gain: 0.18, lfoRate: 0.07, lfoDepth: 70 });
      const s3 = scheduler(() => {
        const t = c.currentTime;
        softDrum(c, bus, t, 68 + Math.random() * 8, 0.28);
        softDrum(c, bus, t + 1.4, 64 + Math.random() * 8, 0.18);
        templeBell(c, bus, t + 2.6, 116 + Math.random() * 18);
      }, 13000, 24000);
      return () => {
        s1();
        s2();
        s3();
      };
    },
  },
  rain: {
    id: "rain",
    name: "雨打禪房",
    category: "break",
    start(c, bus) {
      const s1 = noiseLayer(c, bus, { type: "bandpass", freq: 2400, q: 0.4, gain: 0.16, lfoRate: 0.17, lfoDepth: 600 });
      const s2 = noiseLayer(c, bus, { type: "lowpass", freq: 600, gain: 0.3, lfoRate: 0.09, lfoDepth: 120 });
      const s3 = scheduler(() => droplet(c, bus, c.currentTime), 350, 1400);
      const s4 = scheduler(() => bowlToBus(c, bus, 523, 0.14), 26000, 50000);
      return () => {
        s1();
        s2();
        s3();
        s4();
      };
    },
  },
  wind: {
    id: "wind",
    name: "林間風鈴",
    category: "break",
    start(c, bus) {
      const s1 = noiseLayer(c, bus, { type: "lowpass", freq: 500, gain: 0.32, lfoRate: 0.05, lfoDepth: 260 });
      const s2 = scheduler(() => {
        const n = 1 + Math.floor(Math.random() * 3);
        for (let i = 0; i < n; i++) {
          chime(
            c,
            bus,
            c.currentTime + i * (0.12 + Math.random() * 0.25),
            PENTA[Math.floor(Math.random() * PENTA.length)],
          );
        }
      }, 2500, 8000);
      return () => {
        s1();
        s2();
      };
    },
  },
  "dawn-chime": {
    id: "dawn-chime",
    name: "晨鐘清磬",
    category: "focus",
    start(c, bus) {
      const s1 = dronePad(c, bus, [130.8, 196, 261.6], 0.045);
      const s2 = noiseLayer(c, bus, { type: "highpass", freq: 1200, gain: 0.045, lfoRate: 0.05, lfoDepth: 300 });
      const s3 = scheduler(() => {
        const t = c.currentTime;
        templeBell(c, bus, t, 148 + Math.random() * 12);
        chime(c, bus, t + 4 + Math.random(), PENTA[Math.floor(Math.random() * PENTA.length)]);
      }, 18000, 34000);
      return () => {
        s1();
        s2();
        s3();
      };
    },
  },
  "sutra-drone": {
    id: "sutra-drone",
    name: "梵唱低迴",
    category: "focus",
    start(c, bus) {
      const s1 = dronePad(c, bus, [98, 146.8, 196, 293.7], 0.075);
      const s2 = noiseLayer(c, bus, { type: "bandpass", freq: 320, q: 1.2, gain: 0.07, lfoRate: 0.04, lfoDepth: 90 });
      const s3 = scheduler(() => bowlToBus(c, bus, 196, 0.16), 16000, 32000);
      return () => {
        s1();
        s2();
        s3();
      };
    },
  },
  "pine-guqin": {
    id: "pine-guqin",
    name: "松風古琴",
    category: "focus",
    start(c, bus) {
      const s1 = noiseLayer(c, bus, { type: "lowpass", freq: 480, gain: 0.24, lfoRate: 0.06, lfoDepth: 220 });
      const s2 = scheduler(() => {
        const t = c.currentTime;
        const n = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < n; i++) pluck(c, bus, t + i * (0.45 + Math.random() * 0.35), GUQIN[Math.floor(Math.random() * GUQIN.length)], 0.09);
      }, 6500, 15000);
      return () => {
        s1();
        s2();
      };
    },
  },
  monastery: {
    id: "monastery",
    name: "修道院鐘影",
    category: "break",
    start(c, bus) {
      const s1 = dronePad(c, bus, [87.3, 130.8, 174.6, 261.6], 0.055);
      const s2 = noiseLayer(c, bus, { type: "lowpass", freq: 360, gain: 0.08, lfoRate: 0.03, lfoDepth: 80 });
      const s3 = scheduler(() => templeBell(c, bus, c.currentTime, 98 + Math.random() * 10), 22000, 42000);
      return () => {
        s1();
        s2();
        s3();
      };
    },
  },
  "aegean-lyre": {
    id: "aegean-lyre",
    name: "愛琴海豎琴",
    category: "break",
    start(c, bus) {
      const s1 = noiseLayer(c, bus, { type: "lowpass", freq: 620, gain: 0.22, lfoRate: 0.08, lfoDepth: 260 });
      const s2 = scheduler(() => {
        const t = c.currentTime;
        const n = 3 + Math.floor(Math.random() * 4);
        for (let i = 0; i < n; i++) pluck(c, bus, t + i * 0.22, LYRE[Math.floor(Math.random() * LYRE.length)], 0.07);
      }, 4200, 10000);
      return () => {
        s1();
        s2();
      };
    },
  },
};

const SYNTH_ORDER: readonly SynthKey[] = [
  "stream",
  "bell",
  "dawn-chime",
  "sutra-drone",
  "pine-guqin",
  "rain",
  "wind",
  "monastery",
  "aegean-lyre",
];

function bowlToBus(c: AudioContext, bus: AudioNode, f0: number, amp: number): void {
  const partials: [number, number, number][] = [
    [1, amp, 5],
    [2.69, amp * 0.4, 3.6],
  ];
  partials.forEach(([r, a, d]) => {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = f0 * r;
    const g = c.createGain();
    const t = c.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(a, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g).connect(bus);
    o.start(t);
    o.stop(t + d + 0.1);
  });
}

let mediaEl: HTMLAudioElement | null = null;
let mediaNode: MediaElementAudioSourceNode | null = null;

function playRemote(c: AudioContext, streamUrl: string): TrackStopper {
  if (!mediaEl) {
    mediaEl = new Audio();
    mediaEl.preload = "auto";
    mediaEl.loop = true;
    mediaEl.crossOrigin = "anonymous";
    mediaNode = c.createMediaElementSource(mediaEl);
    mediaNode.connect(ambientBus!);
  }
  if (mediaEl.src !== streamUrl) {
    try {
      mediaEl.pause();
    } catch {
      /* */
    }
    mediaEl.src = streamUrl;
  }
  const p = mediaEl.play();
  if (p && typeof p.catch === "function") p.catch(() => {});
  return () => {
    try {
      mediaEl?.pause();
    } catch {
      /* */
    }
  };
}

function synthTrackForKey(key: string | null | undefined): SynthTrack {
  return (key && SYNTH_TRACKS[key as SynthKey]) || SYNTH_TRACKS.stream;
}

function createDefaultCatalog(): Track[] {
  return SYNTH_ORDER.map((key) => {
    const t = SYNTH_TRACKS[key];
    return {
      id: `synth-${key}`,
      name: t.name,
      category: t.category,
      source: "synth",
      synthKey: key,
    };
  });
}

let catalog: Track[] = createDefaultCatalog();

function updateMediaSession(track: Track | null, playing: boolean): void {
  const mediaSession = navigator.mediaSession;
  if (!mediaSession) return;
  try {
    mediaSession.metadata = new MediaMetadata({
      title: track?.name ?? "佛系番茄鐘",
      artist: "BuddhaTomato",
      album: track ? "梵音" : "一呼一吸 · 自在當下",
      artwork: [
        { src: "/pwa-192x192.png", sizes: "192x192", type: "image/png" },
        { src: "/pwa-512x512.png", sizes: "512x512", type: "image/png" },
      ],
    });
    mediaSession.playbackState = playing ? "playing" : "paused";
  } catch {
    /* Media Session is optional and may reject unsupported metadata. */
  }
}

export const ZenAudio = {
  get tracks(): Array<Pick<Track, "id" | "name" | "category" | "source">> {
    return catalog.map((t) => ({
      id: t.id,
      name: t.name,
      category: t.category,
      source: t.source,
    }));
  },
  sfx: {
    woodfish: (): void => woodfish(),
    bowl: (): void => bowl(undefined, 520, 1),
  },
  /**
   * 合併伺服器回來的曲目清單。後端 schema 為：
   *   { id, title, category, durationSeconds, source: 'synth'|'r2', synthKey, streamUrl }
   * 規則：以 id 為主鍵，server 同 id 覆蓋本地（但若 server 是 synth 來源，
   * 仍 fallback 到本地 synthKey 對應的合成函式）。新 id 直接 append。
   */
  setRemoteCatalog(serverTracks: unknown): void {
    if (!Array.isArray(serverTracks)) return;
    const byId = new Map<string, Record<string, unknown>>(
      serverTracks.map((t: unknown) => [
        String((t as { id: unknown }).id),
        t as Record<string, unknown>,
      ]),
    );
    const merged: Track[] = [];
    const taken = new Set<string>();
    for (const local of catalog) {
      const s = byId.get(local.id);
      if (s) {
        merged.push({
          id: String(s.id),
          name: (s.title as string) || local.name,
          category: s.category as "focus" | "break",
          source: s.source as "synth" | "r2",
          synthKey: ((s.synthKey as string) || local.synthKey) ?? null,
          streamUrl: (s.streamUrl as string) || null,
        });
        taken.add(String(s.id));
      } else {
        merged.push(local);
      }
    }
    for (const s of serverTracks as Array<Record<string, unknown>>) {
      const id = String(s.id);
      if (taken.has(id)) continue;
      merged.push({
        id,
        name: s.title as string,
        category: s.category as "focus" | "break",
        source: s.source as "synth" | "r2",
        synthKey: ((s.synthKey as string) || null) as string | null,
        streamUrl: ((s.streamUrl as string) || null) as string | null,
      });
    }
    catalog = merged;
  },
  play(trackId: string): string | null {
    const c = ac();
    ZenAudio.stop();
    const tr = catalog.find((t) => t.id === trackId) || catalog[0];
    if (!tr) return null;

    const bus = ambientBus!;
    bus.gain.cancelScheduledValues(c.currentTime);
    bus.gain.setValueAtTime(0.0001, c.currentTime);
    bus.gain.exponentialRampToValueAtTime(Math.max(volume, 0.0001), c.currentTime + 1.2);

    if (tr.source === "r2" && tr.streamUrl) {
      current = { id: tr.id, stop: playRemote(c, tr.streamUrl) };
    } else {
      const synthKey = tr.synthKey ?? "";
      const synth = synthTrackForKey(synthKey);
      current = { id: tr.id, stop: synth.start(c, bus) };
    }
    updateMediaSession(tr, true);
    return tr.id;
  },
  stop(): void {
    if (current) {
      const tr = catalog.find((t) => t.id === current?.id) ?? null;
      current.stop();
      current = null;
      updateMediaSession(tr, false);
    }
  },
  isPlaying(): boolean {
    return !!current;
  },
  currentId(): string | null {
    return current ? current.id : null;
  },
  setVolume(v: number): void {
    volume = v;
    if (ambientBus && ctx) {
      ambientBus.gain.setTargetAtTime(Math.max(v, 0.0001), ctx.currentTime, 0.1);
    }
  },
  getVolume(): number {
    return volume;
  },
};

// Back-compat shim for any inline HTML still using window.ZenAudio.
window.ZenAudio = ZenAudio;
